<script setup lang="ts">
import SmallModel from '@/components/Interface/Modal/ModelSizes/SmallModel.vue'
import FormSelect from '@/components/Elements/Forms/FormSelect.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import Backend from '@/models/backend.ts'
import { type Ref, ref, onMounted, computed } from 'vue'
import type ProductionObject from '@/types/ProductionObject.ts'
import { useAssetStore } from '@/stores/assetStore.ts'
import BuildingEffects from '@/components/Sections/Planets/PlanetTabs/Generic/BuildingEffects.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import GenericIconWithNumber from '@/components/Elements/Images/Icons/GenericIconWithNumber.vue'
import FormInput from '@/components/Interface/Modal/Forms/FormInput.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// Get the planet data
const gameStore = useGameStore()
const assetStore = useAssetStore()

const backend = new Backend()

const queuePosition = ref('end_of_queue')
const queuePositionOptions = computed(() => [
  { value: 'after_current', label: t('modals.buildBuilding.queueOptions.afterCurrent') },
  { value: 'end_of_queue', label: t('modals.buildBuilding.queueOptions.endOfQueue') },
  { value: 'replace_clear', label: t('modals.buildBuilding.queueOptions.replaceClear') },
  { value: 'replace_keep', label: t('modals.buildBuilding.queueOptions.replaceKeep') }
])
const searchTerm = ref('')

/**
 * Set the production objectModals
 */
const setProductionObject = (productionObjectId: number) => {
  backend.setPlanetsProductionObject(
    gameStore.modalData.planetIds,
    productionObjectId,
    queuePosition.value
  )
  gameStore.setTinySuccessToastMessage(t('modals.buildBuilding.addedToQueue'))
}

const availableProductionObjects: Ref<ProductionObject[]> = ref([])

/**
 * Calculate the available production objects for the current planet
 **/
const updateList = () => {
  // Get all available production objects for the player
  const productionObjectsMap = gameStore.playerProductionObjects

  if (productionObjectsMap && productionObjectsMap.size > 0) {
    // Sort by cost
    availableProductionObjects.value = Array.from(productionObjectsMap.entries())
      .map(([id]) => {
        const productionObject = assetStore.findProductionObjectById(id)
        if (!productionObject) {
          throw new Error(`Production object with ID ${id} not found.`)
        }
        return productionObject
      })
      .sort((a, b) => a.type.localeCompare(b.type))
  }
}

// Helper to get translated building name
const getBuildingName = (productionObject: ProductionObject) => {
  if (productionObject.type === 'building') {
    return t(`buildings.buildings.${productionObject.objectId}.name`)
  }
  return productionObject.productionObjectName
}

// Filter buildings based on search term
const filteredBuildings = computed(() => {
  if (!searchTerm.value) {
    return availableProductionObjects.value
  }

  return availableProductionObjects.value.filter((productionObject) =>
    getBuildingName(productionObject).toLowerCase().includes(searchTerm.value.toLowerCase())
  )
})

onMounted(() => {
  updateList()
})
</script>

<template>
  <SmallModel :title="t('modals.buildBuilding.title')">
    <div class="">
      <!-- Search input -->
      <div class="px-2 pt-2 pb-1">
        <FormInput
          v-model="searchTerm"
          :placeholder="t('modals.buildBuilding.searchPlaceholder')"
        />
      </div>
      <div style="max-height: 25rem" class="overflow-y-scroll bg-metal-950 pt-0.5">
        <template v-for="productionObject in filteredBuildings" :key="productionObject.id">
          <div v-if="productionObject.type === 'building'" class="flex h-12 bg-metal-900 mb-0.5 items-center">
            <div class="ps-2 text-metal-300 text-left flex-1">
              {{ getBuildingName(productionObject) }}
            </div>
            <div class="hidden md:flex w-32 justify-start">
              <BuildingEffects
                :building="assetStore.findBuildingByCode(productionObject.objectId)"
              >
              </BuildingEffects>
            </div>
            <div class="pe-2 flex items-center w-20 justify-end">
              <ActionButton @click="setProductionObject(productionObject.id)">
                <GenericIconWithNumber
                  :amount="productionObject.productionCost"
                  type="production"
                  text-color="text-metal-300"
                />
              </ActionButton>
            </div>
          </div>
        </template>
      </div>
      <div class="flex justify-center pb-2 pt-2 text-subtext">
        <div class="flex items-center pr-2">{{ t('modals.buildBuilding.queuePosition') }}</div>
        <div class="ps-2">
          <FormSelect
            v-model="queuePosition"
            :options="queuePositionOptions"
          />
        </div>
      </div>
    </div>
  </SmallModel>
</template>

<style scoped lang="scss">
</style>
